/**
 * Feature-gate overrides for everyone in one tenant — "off for RG Academy, on
 * for everyone else" without touching the gate's global value or its rings.
 *
 * Each gate is tri-state: inherit the gate (its global value and rings), or pin
 * it ON/OFF for the tenant. A user's own override still beats the tenant's, so
 * one person can be exempted from what their institute chose. Edits are staged
 * and saved together, as FeatureGatesTab does for a user.
 *
 * Two kinds of gate are left out of the editable list, as the server refuses
 * both: a kill switch (`allowOverride: false`) ignores every override, and a
 * global-resolution gate is read with no user, so there is no tenant either.
 * See docs/FeatureDesigns/multi-tenancy/tenant-settings-target.md §4.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { featureGateResolution } from '@padcanvas/shared';
import { DataTable, SearchFilterBar, type ColumnDef, type SortState } from '@shared-ui/components';
import { adminApi } from '../../../api/adminApi';
import type { FeatureGateDefinition } from '../../../api/userApi';
import { tenantsApi } from '../../../api/tenantsApi';

type Override = 'inherit' | 'on' | 'off';
const OVERRIDE_RANK: Record<Override, number> = { on: 0, off: 1, inherit: 2 };

interface Row {
    name: string;
    def: FeatureGateDefinition;
    locked: 'kill-switch' | 'global' | null;
}

function gateSummary(def: FeatureGateDefinition): string {
    if (!def.enabled) return 'OFF';
    return def.enabledForRings?.length ? `ON for ${def.enabledForRings.join(', ')}` : 'ON';
}

export function TenantGatesCard({ tenantId }: { tenantId: string }) {
    const queryClient = useQueryClient();
    const { data: gateData, isLoading: gatesLoading } = useQuery({
        queryKey: ['featureGates'],
        queryFn: () => adminApi.getFeatureGates(),
    });
    const { data: settings, isLoading: settingsLoading } = useQuery({
        queryKey: ['tenant-settings', tenantId],
        queryFn: () => tenantsApi.settings(tenantId),
    });

    const rows: Row[] = useMemo(() => Object.entries(gateData?.gates ?? {}).map(([name, def]) => ({
        name,
        def,
        locked: featureGateResolution(name) === 'global' ? 'global' : def.allowOverride === false ? 'kill-switch' : null,
    })), [gateData]);

    const saved = useMemo(() => {
        const map: Record<string, Override> = {};
        for (const r of rows) {
            const v = settings?.featureGates[r.name];
            map[r.name] = v === undefined ? 'inherit' : v ? 'on' : 'off';
        }
        return map;
    }, [rows, settings]);

    const [draft, setDraft] = useState<Record<string, Override>>({});
    useEffect(() => { setDraft(saved); }, [saved]);

    const [filters, setFilters] = useState<Record<string, string>>({ search: '' });
    const [sort, setSort] = useState<SortState>({ sortBy: 'override', sortOrder: 'asc' });

    const save = useMutation({
        mutationFn: () => {
            // Only what changed goes in the patch; "inherit" is sent as null so
            // the override is removed rather than set.
            const featureGates: Record<string, boolean | null> = {};
            for (const [name, state] of Object.entries(draft)) {
                if (state === saved[name]) continue;
                featureGates[name] = state === 'inherit' ? null : state === 'on';
            }
            return tenantsApi.updateSettings(tenantId, { featureGates });
        },
        onSuccess: next => queryClient.setQueryData(['tenant-settings', tenantId], next),
    });

    const needle = (filters.search ?? '').trim().toLowerCase();
    const visible = useMemo(() => (
        needle
            ? rows.filter(r => `${r.name} ${r.def.description ?? ''}`.toLowerCase().includes(needle))
            : rows
    ), [rows, needle]);

    const columns: ColumnDef<Row>[] = useMemo(() => [
        {
            key: 'name', header: 'Gate', sortable: true, hideable: false,
            sortValue: r => r.name,
            render: r => (
                <>
                    <strong style={{ fontFamily: 'monospace' }}>{r.name}</strong>
                    {r.def.description && (
                        <>
                            <br />
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.def.description}</span>
                        </>
                    )}
                </>
            ),
        },
        {
            key: 'gate', header: 'Gate value', sortable: true,
            sortValue: r => (r.def.enabled ? 0 : 1),
            render: r => <span style={{ fontSize: 12 }}>{gateSummary(r.def)}</span>,
        },
        {
            // Sorted on the SAVED override, never the draft, so a row does not
            // leap out from under the cursor the moment it is changed.
            key: 'override', header: 'Tenant override', sortable: true,
            sortValue: r => OVERRIDE_RANK[saved[r.name] ?? 'inherit'],
            render: r => {
                const current = draft[r.name] ?? 'inherit';
                const changed = current !== saved[r.name];
                return (
                    <select
                        value={current}
                        // A stale override on a now-locked gate can still be cleared.
                        disabled={r.locked !== null && current === 'inherit'}
                        title={r.locked === 'global'
                            ? 'Read before anyone signs in — there is no tenant to override it for'
                            : r.locked === 'kill-switch' ? 'Kill switch — this gate ignores every override' : undefined}
                        onChange={e => setDraft(d => ({ ...d, [r.name]: e.target.value as Override }))}
                        style={{ fontSize: 12, borderColor: changed ? 'var(--color-primary)' : undefined }}
                    >
                        <option value="inherit">— inherit —</option>
                        {r.locked === null && <option value="on">ON</option>}
                        {r.locked === null && <option value="off">OFF</option>}
                    </select>
                );
            },
        },
    ], [draft, saved]);

    if (gatesLoading || settingsLoading) return <div className="loading" style={{ padding: 16 }}>Loading feature gates…</div>;

    const dirty = rows.some(r => (draft[r.name] ?? 'inherit') !== saved[r.name]);
    const pinned = rows.filter(r => saved[r.name] !== 'inherit').length;

    return (
        <div className="features-section">
            <p className="tenant-detail-note">
                {pinned === 0
                    ? 'No overrides: every gate resolves for this tenant as it does for everyone.'
                    : `${pinned} gate${pinned === 1 ? '' : 's'} pinned for this tenant.`}
                {' '}A user's own override still wins over the tenant's.
            </p>
            <SearchFilterBar
                filters={[]}
                values={filters}
                onChange={setFilters}
                hideFetch
                searchPlaceholder="Search gates…"
                trailingActions={(
                    <>
                        {save.isError && (
                            <span style={{ color: 'var(--color-error)', fontSize: 12 }}>
                                {(save.error as Error).message}
                            </span>
                        )}
                        {!dirty && save.isSuccess && (
                            <span style={{ color: 'var(--color-success)', fontSize: 12 }}>Saved</span>
                        )}
                        {dirty && !save.isPending && (
                            <button className="btn" onClick={() => setDraft(saved)}>Discard</button>
                        )}
                        <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                            {save.isPending ? 'Saving…' : 'Save overrides'}
                        </button>
                    </>
                )}
            />
            <DataTable
                columns={columns}
                data={visible}
                rowKey={r => r.name}
                sort={sort}
                onSortChange={setSort}
                emptyMessage={needle ? `No gate matches “${filters.search}”.` : 'No feature gates defined.'}
            />
        </div>
    );
}
